<template>
	<Layout>
		<Header>
			<b-button v-if="permissionEnabled('links', 'create')" type="is-primary create" rounded @click="modalCreate($event)">
				<svg-icon icon="plus" class="icon is-small"></svg-icon>
				<span>{{ $t('buttons.add') }}</span>
			</b-button>
		</Header>
		<section v-if="!errored" class="filter">
			<div class="filter__wrapper">
				<b-field :label="$t('labels.order')" label-position="on-border">
					<b-select v-model="order" :placeholder="$tc('fields.name')" @input="filterByOrder">
						<option selected value="name">{{ $tc('fields.name') }}</option>
						<option value="created_at">{{ $t('fields.date') }}</option>
					</b-select>
				</b-field>
				<b-field>
					<b-input :placeholder="$t('buttons.search')" type="search" icon-right="magnify" @input="findByName" v-model="name"></b-input>
				</b-field>
				<p v-if="!loading" class="shortened">{{ links.length }} links encurtados</p>
			</div>
		</section>
		<Error v-if="errored" :back="true" />
		<Results v-if="links.length == 0 && !loading" />
		<div v-if="loading" class="columns is-multiline">
			<div v-for="r in placeholder" :key="r" class="column is-12-mobile is-6-tablet is-4-desktop">
				<Placeholder />
			</div>
		</div>
		<transition-group v-if="!loading" name="filtering" class="filtering columns is-multiline" tag="div">
			<div v-for="t in links" :key="t.id" class="list-item column is-12-mobile is-6-tablet is-6-desktop">
				<article class="block">
					<div class="block__content">
						<h3 class="block__name" translate="no">{{ t.name }}</h3>
						<p class="block__email code">{{ t.url }} ({{ t.clicks }} cliques)</p>
						<!-- <p class="block__email" @click.self="modalEdit(t.id)">{{ format(t.created_at) }} • {{ timeTo(t.created_at) }}</p> -->
						<b-tooltip class="block__copy" type="is-primary" position="is-right" label="Copiar">
							<span class="block__id" @click="copyToClipbboard(t.short)">https://short.orbital.company/{{ t.hash }}</span>
						</b-tooltip>
						<div class="block__users">
							<b-tooltip v-if="t.user" :label="t.user.full_name">
								<img :src="t.user.avatar" />
							</b-tooltip>
						</div>
					</div>
					<Trigger :id="t.id" />
				</article>
			</div>
		</transition-group>
	</Layout>
</template>

<script>
import Layout from '@/layouts/Default'
import Header from '@/components/Header'
import Modal from '@/components/modals/Link'
import Placeholder from '@/components/placeholders/Role'
import Trigger from '@/components/triggers/Link'
import Icon from '@/components/Icon'
import Error from '@/components/Error'
import Results from '@/components/Results'
import Api from '@/services/api'
import eventHub from '@/services/eventHub'
import { create, update } from '@/mixins/modal'
import { responsive } from '@/mixins/responsive'
import { errorToast, successToast } from '@/mixins/toast'
import { mapGetters } from 'vuex'
import mixinApp from '@/mixins/app'

export default {
	name: 'Link',
	mixins: [
		responsive,
		mixinApp
	],
	components: {
		Layout,
		Header,
		Error,
		Results,
		Placeholder,
		Trigger,
		'svg-icon': Icon
	},
	data() {
		return {
			loading: true,
			placeholder: 12,
			order: 'name',
			name: '',
			errored: false,
			links: []
		}
	},
	methods: {
		modalCreate() {
			if (this.permissionEnabled('links', 'create')) {
				create(this, 'links', Modal, 'New')
			}
		},
		modalEdit(id) {
			if (this.permissionEnabled('links', 'edit')) {
				update(this, 'links', id, Modal, 'Edit')
			}
		},
		async findAll() {
			this.links = []
			this.loading = true
			try {
				const response = await Api.get('links/findAll')
				if (response.status === 200) {
					this.links = response.data
				}
			} catch (e) {
				console.log(e)
				errorToast(this.$t('alerts.load_error'))
			} finally {
				this.loading = false
			}
        },
		async filterByOrder() {
			this.links = []
			this.loading = true
			try {
				const response = await Api.post('links/filterByOrder', { order: this.order })
				if (response.status === 200) {
					this.links = response.data
				}
			} catch (e) {
				console.log(e)
			} finally {
				this.loading = false
			}
		},
		async findByName() {
			this.links = []
			this.loading = true
			try {
				const empty = /^\s*$/
				if (!empty.test(this.name)) {
					const response = await Api.post('links/findByName', { name: this.name })
					if (response.status === 200) {
						this.links = response.data
					}
				} else {
					await this.findAll()
				}
			} catch (e) {
				console.log(e)
			} finally {
				this.loading = false
			}
		},
		copyToClipbboard(text) {
			navigator.clipboard.writeText(text)
			successToast('Copiado com sucesso')
		},
	},
	mounted() {
		eventHub.$on('edit-modal-link', obj => {
			update(this, 'links', obj.id, Modal, 'Edit')
		})

		eventHub.$on('reload-links', () => {
			this.findAll()
		})

		eventHub.$on('delete-link', obj => {
			this.$buefy.dialog.alert({
				size: 'is-delete',
				type: 'is-outlined is-primary',
				title: this.$t('labels.warning'),
				message: '<span>' + this.$t('alerts.delete.confirmation') + '<span>',
				canCancel: true,
				focusOn: 'cancel',
				cancelText: this.$t('buttons.no'),
				confirmText: this.$t('buttons.yes'),
				onConfirm: async () => {
					try {
						const response = await Api.delete(`links/destroy/${obj.id}`)
						const { status } = response
						if (status === 200) {
							successToast(this.$t('alerts.delete.success', [this.$tc('menu.links')]))
							this.findAll()
						}
					} catch (e) {
						console.log(e)
						errorToast(this.$t('alerts.delete.error', [this.$tc('menu.links')]))
					}
				}
			})
		})

        this.findAll()
    },
	destroyed() {
		eventHub.$off('edit-modal-type')
		eventHub.$off('reload-type')
		eventHub.$off('delete-type')
	},
	computed: {
		...mapGetters('user', ['permissionEnabled']),
	}
}
</script>
